import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { css } from '@styled-system/css'

import CommonLink from 'components_v2/CommonLink'
import { Box, Button, Flex, P, Tag } from 'ui/bend'
import { Image as ImageAcro } from 'ui/acro'

import {
  formatEventBadge,
  formatEventDate,
  formatEventLocation,
  formatEventTimeRange
} from './formatEvent'
import EventRegisterButton from './EventRegisterButton'
import { useNavigate } from 'react-router-dom'

const PLACEHOLDER_IMAGE =
  'https://alomoves.s3.amazonaws.com/manual_uploads/shared/search/placeholder-cards/rectangle-card.jpg'

const Card = styled(Box)(
  css({
    display: 'flex',
    flexDirection: ['column', null, 'row'],
    backgroundColor: 'base',
    border: '1px solid',
    borderColor: 'grey2',
    overflow: 'hidden',
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)'
    }
  })
)

const ImageWrap = styled(Box)(
  css({
    position: 'relative',
    flexShrink: 0,
    width: ['100%', null, '340px'],
    aspectRatio: ['16 / 10', null, 'auto'],
    minHeight: [null, null, '240px'],
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: '100%',
      minHeight: ['220px', null, '100%'],
      objectFit: 'cover',
      display: 'block'
    }
  })
)

const DateBadge = styled(Flex)(
  css({
    position: 'absolute',
    top: '16px',
    left: '16px',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
    backgroundColor: 'base',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)'
  })
)

const BadgeMonth = styled(P)(
  css({
    m: 0,
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    lineHeight: 1.2,
    textTransform: 'uppercase',
    color: 'grey4'
  })
)

const BadgeDay = styled(P)(
  css({
    m: 0,
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: 1.1,
    color: 'primary'
  })
)

const Content = styled(Flex)(
  css({
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    p: ['24px', null, '32px 36px'],
    gap: '20px'
  })
)

const Title = styled(P)(
  css({
    m: 0,
    color: 'primary',
    fontSize: ['18px', null, '20px'],
    fontWeight: 600,
    lineHeight: '135%'
  })
)

const MetaList = styled(Flex)(
  css({
    flexDirection: 'column',
    gap: '8px',
    mt: '12px'
  })
)

const Meta = styled(P)(
  css({
    color: 'grey4',
    fontSize: ['14px', null, '15px'],
    lineHeight: '150%',
    m: 0
  })
)

const TagRow = styled(Flex)(
  css({
    flexWrap: 'wrap',
    gap: '8px',
    mt: '12px'
  })
)

const TitleLink = styled(CommonLink)(
  css({
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  })
)

const ClickableImageWrap = styled(ImageWrap)(
  css({
    cursor: 'pointer'
  })
)

const Actions = styled(Flex)(
  css({
    flexDirection: ['column', 'row'],
    gap: '12px',
    pt: '20px',
    borderTop: '1px solid',
    borderColor: 'grey2'
  })
)

const EventCard = ({ event, onRegistrationChange }) => {
  const { id, title, coverUrl, startAt, endAt, timezone, tags = [], isPast } = event
  const detailPath = `/events/${id}`
  const dateLabel = formatEventDate(startAt, timezone)
  const timeLabel = formatEventTimeRange(startAt, endAt, timezone)
  const locationLabel = formatEventLocation(event)
  const dateBadge = formatEventBadge(startAt, timezone)
  const navigate = useNavigate()

  return (
    <Card>
      <ClickableImageWrap as={CommonLink} to={detailPath}>
        <ImageAcro
          src={coverUrl || PLACEHOLDER_IMAGE}
          alt={title}
          objectFit='cover'
          onError={(elem) => {
            elem.currentTarget.src = PLACEHOLDER_IMAGE
          }}
        />
        {dateBadge && (
          <DateBadge aria-hidden='true'>
            <BadgeMonth>{dateBadge.month}</BadgeMonth>
            <BadgeDay>{dateBadge.day}</BadgeDay>
          </DateBadge>
        )}
      </ClickableImageWrap>

      <Content>
        <Box>
          <Title>
            <TitleLink to={detailPath}>{title}</TitleLink>
          </Title>

          <MetaList>
            {dateLabel && <Meta>{dateLabel}</Meta>}
            {timeLabel && <Meta>{timeLabel}</Meta>}
            {locationLabel && <Meta>{locationLabel}</Meta>}
          </MetaList>

          {tags.length > 0 && (
            <TagRow>
              {tags.map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagRow>
          )}
        </Box>

        <Actions>
          <EventRegisterButton
            {...event}
            eventId={event.id}
            eventTitle={event.title}
            registrationQuestions={event.registrationQuestions}
            registrationDefaults={event.registrationDefaults}
            registrationStatus={event.registrationStatus}
            isPast={isPast}
            onRegistrationChange={onRegistrationChange}
          />
          <Button
            onClick={() => navigate(detailPath)}
            variant='ghost'
            width={['100%', 'auto']}
            minWidth='140px'
          >
            View Details
          </Button>
        </Actions>
      </Content>
    </Card>
  )
}

EventCard.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    coverUrl: PropTypes.string,
    startAt: PropTypes.string,
    endAt: PropTypes.string,
    timezone: PropTypes.string,
    locationType: PropTypes.string,
    city: PropTypes.string,
    region: PropTypes.string,
    venue: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    isPast: PropTypes.bool,
    lumaUrl: PropTypes.string,
    registrationStatus: PropTypes.oneOf([
      'none',
      'registered',
      'waitlisted',
      'pending',
      'declined'
    ]),
    registrationQuestions: PropTypes.arrayOf(PropTypes.object),
    registrationDefaults: PropTypes.shape({
      nameRequirement: PropTypes.oneOf(['full-name', 'first-last']),
      phoneNumberRequirement: PropTypes.oneOf(['optional', 'required'])
    })
  }).isRequired,
  onRegistrationChange: PropTypes.func
}

export default EventCard
